import DateFnsUtils from "@date-io/date-fns"
import { CssBaseline, ThemeProvider } from "@material-ui/core"
import { MuiPickersUtilsProvider } from "@material-ui/pickers"
import dayjs from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import isSameOrAfter from "dayjs/plugin/isSameOrAfter"
import isToday from "dayjs/plugin/isToday"
import weekday from "dayjs/plugin/weekday"
import Cookies from "js-cookie"
import { Suspense, useEffect } from "react"
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { RecoilRoot, useRecoilSnapshot } from "recoil"
import ErrorBoundary from "./components/organisms/ErrorBoundary"
import Routes from "./components/screens/Routes"
import { ROUTES } from "./hooks/useNavigation"
import { setupLogRocket } from "./utils/logrocket"
import { theme } from "./utils/theme"
import { STORAGE } from "./utils/types"

setupLogRocket()

dayjs.extend(customParseFormat)
dayjs.extend(weekday)
dayjs.extend(isToday)
dayjs.extend(isSameOrAfter)

function DebugObserver() {
  const snapshot = useRecoilSnapshot()
  useEffect(() => {
    console.debug("The following atoms were modified:")
    for (const node of snapshot.getNodes_UNSTABLE({ isModified: true })) {
      console.debug(node.key, snapshot.getLoadable(node))
    }
  }, [snapshot])

  return null
}

function isAuthFailure(error: unknown): boolean {
  if (!error || typeof error !== "object") {
    return false
  }
  const status =
    (error as { response?: { status?: number } }).response?.status ??
    (error as { status?: number }).status
  if (status === 401 || status === 403) {
    return true
  }
  const message = String((error as { message?: string }).message ?? "")
  return /jwt|unauthorized|not authenticated|invalid.?token/i.test(message)
}

/** Only clear session on auth failures — never on ordinary query/UI errors. */
function onError(error: unknown) {
  console.error(error)
  if (!isAuthFailure(error)) {
    return
  }
  queryClient.invalidateQueries()
  queryClient.getQueryCache().clear()
  Cookies.remove(STORAGE.JWT)
  Cookies.remove(STORAGE.ROLE)
  Cookies.remove(STORAGE.USER_ID)
  window.location.pathname = ROUTES.LOGGED_OUT
}

// TODO: add onError handler to queries.
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError,
  }),
  defaultOptions: { mutations: { onError } },
})

function App() {
  return (
    <div className="app-container">
      <div className="app-content">
        <RecoilRoot>
          <ErrorBoundary>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools />
              <Suspense fallback="Loading...">
                <DebugObserver />
                <ThemeProvider theme={theme}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <CssBaseline />
                    <Routes />
                  </MuiPickersUtilsProvider>
                </ThemeProvider>
              </Suspense>
            </QueryClientProvider>
          </ErrorBoundary>
        </RecoilRoot>
      </div>
    </div>
  )
}

export default App
